import {Component, OnDestroy, OnInit} from '@angular/core';
import {HttpErrorResponse, HttpResponse} from '@angular/common/http';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {StandardDocumentContext, ViewDocxService} from '../../../services/view-docx.service';
import {DomSanitizer, SafeResourceUrl} from '@angular/platform-browser';
import {Subject} from 'rxjs/Subject';
import 'rxjs/add/operator/takeUntil';
import {Standard} from '../../../models/standard.model';
import {isStandardVersionConflict, StandardVersionSnapshot} from '../../../services/standard-version';
import {hasServerErrorCode} from '../../../services/server-error-code';
import {isStandardsPhone} from 'app/content-page/my-standards/standards-viewport';

const OFFICE_VIEWER_URL = 'https://view.officeapps.live.com/op/view.aspx?wdMobile=0&src=';
const OFFICE_VIEWER_MIN_WIDTH = 640;
const DEFAULT_VIEWER_SCALE = 1;
const VIEWER_HEIGHT_VH = 80;
const HTTP_CONFLICT = 409;
const HTTP_FORBIDDEN = 403;
const HTTP_MISSING_FILE = 412;
const STANDARD_FILE_NOT_PREPARED = 'STANDARD_FILE_NOT_PREPARED';
const MIN_STANDARD_VERSION = 0;
const NO_DOCUMENT_ERROR = '';
const DOCUMENT_MESSAGE = {
  prepare: 'STANDARD_DOCUMENT_NEEDS_PREPARATION',
  missing: 'STANDARD_DOCUMENT_MISSING',
  access: 'STANDARD_DOCUMENT_ACCESS_DENIED',
  failed: 'STANDARD_DOCUMENT_LOAD_FAILED',
  conflict: 'STANDARD_DOCUMENT_VERSION_CONFLICT'
};

@Component({
  selector: 'app-view-docx',
  templateUrl: './view-docx.component.html',
  styleUrls: ['./view-docx.component.css']
})
export class ViewDocxComponent implements OnInit, OnDestroy {
  isLoaded = false;
  isPhone = isStandardsPhone();
  viewerLoading = false;
  viewSrc = '';
  viewerUrl: SafeResourceUrl | null = null;
  viewerWidth: number | null = null;
  viewerScale = DEFAULT_VIEWER_SCALE;
  viewerHeightVh = VIEWER_HEIGHT_VH;
  tempDocLink = '';
  pending = false;
  canPrepare = false;
  canReload = false;
  errorKey = NO_DOCUMENT_ERROR;
  private context: StandardDocumentContext;
  private snapshot: StandardVersionSnapshot;
  private destroyed$ = new Subject<void>();

  constructor(public activeModal: NgbActiveModal,
              public sanitizer: DomSanitizer,
              public viewDocxService: ViewDocxService) {}

  ngOnInit(): void {
    this.context = this.viewDocxService.readDocumentContext();
    if (this.context.fileLink) { this.setFileLink(this.context.fileLink); }
    else { this.getFileLink(); }
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  getFileLink(): void {
    if (this.pending) { return; }
    const request = this.viewDocxService.getLinkByDocxID(this.context);
    this.canPrepare = false;
    this.canReload = !!(this.context.fileId && this.context.standardId);
    this.errorKey = NO_DOCUMENT_ERROR;
    if (!request) { this.errorKey = DOCUMENT_MESSAGE.missing; return; }
    this.pending = true;
    request.takeUntil(this.destroyed$).subscribe(
      response => this.acceptLink(response), error => this.handleReadError(error)
    );
  }

  prepareDocument(): void {
    if (this.pending || !this.canPrepare || !this.snapshot) { return; }
    this.pending = true;
    this.canPrepare = false;
    this.viewDocxService.prepareDocument(this.context, this.snapshot).takeUntil(this.destroyed$).subscribe(
      response => this.acceptLink(response), error => this.handlePrepareError(error)
    );
  }

  private handleReadError(error: HttpErrorResponse): void {
    this.pending = false;
    if (error.status === HTTP_CONFLICT && hasServerErrorCode(error, STANDARD_FILE_NOT_PREPARED)) {
      this.loadPreparationSnapshot();
      return;
    }
    this.errorKey = this.readErrorKey(error);
  }

  private loadPreparationSnapshot(): void {
    this.pending = true;
    this.viewDocxService.loadDocumentStandard(this.context).takeUntil(this.destroyed$).subscribe(
      response => this.acceptPreparationSnapshot(response),
      error => { this.pending = false; this.errorKey = this.readErrorKey(error); }
    );
  }

  private acceptPreparationSnapshot(response: HttpResponse<Standard>): void {
    this.pending = false;
    const standard = response.body;
    const valid = standard && standard.id === this.context.standardId && standard.version != null
      && Number.isSafeInteger(standard.version) && standard.version >= MIN_STANDARD_VERSION;
    this.snapshot = valid ? {id: standard.id, version: standard.version} : null;
    this.canPrepare = !!this.snapshot;
    this.errorKey = valid ? DOCUMENT_MESSAGE.prepare : DOCUMENT_MESSAGE.failed;
  }

  private handlePrepareError(error: HttpErrorResponse): void {
    this.pending = false;
    this.canPrepare = false;
    this.errorKey = isStandardVersionConflict(error) ? DOCUMENT_MESSAGE.conflict : this.readErrorKey(error);
  }

  private readErrorKey(error: HttpErrorResponse): string {
    if (error && error.status === HTTP_FORBIDDEN) { return DOCUMENT_MESSAGE.access; }
    if (error && error.status === HTTP_MISSING_FILE) { return DOCUMENT_MESSAGE.missing; }
    return DOCUMENT_MESSAGE.failed;
  }

  private acceptLink(response: HttpResponse<string>): void {
    this.pending = false;
    this.canPrepare = false;
    if (response.body) { this.setFileLink(response.body); }
    else { this.errorKey = DOCUMENT_MESSAGE.missing; }
  }

  setFileLink(fileURL: string): void {
    this.errorKey = NO_DOCUMENT_ERROR;
    this.tempDocLink = fileURL;
    this.viewSrc = OFFICE_VIEWER_URL + encodeURIComponent(fileURL);
    this.viewerUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.viewSrc);
    this.isLoaded = true;
    this.viewerLoading = true;
  }

  viewerFrameLoaded(width: number): void {
    this.viewerLoading = false;
    this.fitViewer(width);
  }

  viewerFrameFailed(): void {
    this.viewerLoading = false;
    this.errorKey = DOCUMENT_MESSAGE.failed;
  }

  fitViewer(width: number): void {
    if (!width) { return; }
    this.viewerWidth = Math.max(width, OFFICE_VIEWER_MIN_WIDTH);
    this.viewerScale = width / this.viewerWidth;
    this.viewerHeightVh = VIEWER_HEIGHT_VH / this.viewerScale;
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/views/view-docx/view-docx.component.ts